/**
 * Chat-first homepage entry. Starter actions only prime the real Home Chat
 * composer; the server remains authoritative for intent, confirmation,
 * workflow selection, creation, and controller handoff.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Snackbar,
  Typography,
} from '@mui/material';
import type { AlertColor } from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';

import type { GpcChatWorkflowStageKey } from '../GpcChat/types';
import HomeChatPanel, { type ConstrainedWorkflowEntryState } from './HomeChatPanel';
import ChatExamplesBrowser from '../ChatExamples/ChatExamplesBrowser';
import HomeChatCompletionReconciler from './HomeChatCompletionReconciler';
import HomeChatSearchBar from './HomeChatSearchBar';
import { parseHomeChatMode, type HomeChatMode } from './homeChatMode';
import { getWorkflowOnlyPresentation } from './workflowOnlyPresentation';
import { createSession, routeConstrainedWorkflowTypedEntry } from './api';
import { extractMrRouterResponse } from './mrHandoff';
import { extractGpcRouterResponse, type GpcRouterResponse } from './gpcHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractConstrainedNativeWorkflowEntry } from './constrainedWorkflowProjection';
import type { MrChatSessionView } from '../MrChat/types';
import type {
  HomeChatHandoffContext,
  HomeChatGuidanceCard,
  WorkflowRecommendationType,
} from './types';
import { normalizeHomeChatCards } from './types';

type LauncherSnackbar = { severity: AlertColor; message: string } | null;
type StarterKey = 'commodities' | 'services' | 'construction' | 'construction_generate' | 'construction_verify' | 'price_estimator' | 'gpc' | 'ask';

type StarterAction = {
  key: StarterKey;
  label: string;
  prompt: string;
  workflowTypes: WorkflowRecommendationType[];
  icon: React.ReactNode;
};

/** Only render a router-issued chooser when its opaque selections are usable. */
function isWorkflowOnlyChoice(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const card = value as Record<string, unknown>;
  return typeof card.card_id === 'string' && card.card_id.trim().length > 0
    && typeof card.prompt === 'string' && card.prompt.trim().length > 0
    && Array.isArray(card.options) && card.options.length > 0
    && card.options.every((option) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) return false;
      const item = option as Record<string, unknown>;
      return typeof item.label === 'string' && item.label.trim().length > 0
        && typeof item.selection_key === 'string' && item.selection_key.trim().length > 0;
    });
}

export const HOME_CHAT_STARTERS: StarterAction[] = [
  {
    key: 'commodities',
    label: 'Research a commodity',
    prompt: 'I want to start commodities market research. The product is ',
    workflowTypes: ['market_research'],
    icon: <Inventory2OutlinedIcon fontSize="small" />,
  },
  {
    key: 'services',
    label: 'Plan a service acquisition',
    prompt: 'I need help planning a services acquisition. The service I need is ',
    workflowTypes: [
      'services',
      'complex_services',
      'services_combo',
      'clause_logic',
    ],
    icon: <MiscellaneousServicesOutlinedIcon fontSize="small" />,
  },
  {
    key: 'construction',
    label: 'Start construction',
    prompt: 'I need help planning a construction acquisition. The project is ',
    workflowTypes: [
      'one_time_construction',
      'sow_verification',
      'wage_determination',
      'pnm',
      'fopr',
      'clause_logic',
    ],
    icon: <ConstructionOutlinedIcon fontSize="small" />,
  },
  {
    key: 'gpc',
    label: 'Make a GPC purchase',
    prompt: 'I want to start a GPC purchase. I need to buy ',
    workflowTypes: ['gpc'],
    icon: <CreditCardOutlinedIcon fontSize="small" />,
  },
  {
    key: 'ask',
    label: 'Ask anything',
    prompt: '',
    workflowTypes: [],
    icon: <QuestionAnswerOutlinedIcon fontSize="small" />,
  },
];

// Constrained construction examples stay out of the full CO/CS starter list.
// The shell may prime prompts, but route selection remains server-owned.
const CONSTRAINED_CONSTRUCTION_STARTERS: StarterAction[] = [
  {
    key: 'construction_generate',
    label: 'Generate Construction SOW',
    prompt: 'Generate a construction SOW for ',
    workflowTypes: ['one_time_construction'],
    icon: <ConstructionOutlinedIcon fontSize="small" />,
  },
  {
    key: 'price_estimator',
    label: 'Construction Price Estimator',
    prompt: 'Estimate the price of a construction project: ',
    workflowTypes: ['one_time_construction'],
    icon: <ConstructionOutlinedIcon fontSize="small" />,
  },
  {
    key: 'construction_verify',
    label: 'Verify existing Construction SOW',
    prompt: 'Verify an existing construction SOW',
    workflowTypes: ['sow_verification'],
    icon: <ConstructionOutlinedIcon fontSize="small" />,
  },
];

export interface HomeChatLauncherProps {
  canAccessTraining?: boolean;
  onOpenTraining?: () => void;
  onOpenTrainingSuggestion?: () => void;
  canUseChatbot?: boolean;
  examplesAccessScope?: string;
  onOpenExampleWorkflow?: (flowId: string, title: string, workflowType: string) => void;
  onOpenWorkflow?: (
    flowId: string,
    targetStageHint?: GpcChatWorkflowStageKey,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenMrWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenConstructionSowWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenGeneratedServicesPwsWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenNavigationHandle?: (
    navigationHandle: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onOpenCreatedWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onStartWorkflowType?: (
    workflowType: WorkflowRecommendationType,
    context?: HomeChatHandoffContext,
  ) => void;
  allowedWorkflowTypes?: string[] | null;
  /** Public server projection; it never authorizes a request. */
  homeChatMode?: HomeChatMode | string | null;
  workflowOnlyAudience?: 'ra' | 'ce' | null;
  /** Presentation-only role previews show the permitted shell but never enter it. */
  readOnly?: boolean;
}

const HomeChatLauncher: React.FC<HomeChatLauncherProps> = ({
  canAccessTraining = false,
  onOpenTraining,
  onOpenTrainingSuggestion,
  canUseChatbot = true,
  examplesAccessScope,
  onOpenExampleWorkflow,
  onOpenWorkflow,
  onOpenMrWorkflow,
  onOpenConstructionSowWorkflow,
  onOpenGeneratedServicesPwsWorkflow,
  onOpenNavigationHandle,
  onOpenCreatedWorkflow,
  onStartWorkflowType,
  allowedWorkflowTypes,
  homeChatMode,
  workflowOnlyAudience = null,
  readOnly = false,
}) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [notificationEntryVersion, setNotificationEntryVersion] = useState(0);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [initialSessionId, setInitialSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [selectedStarter, setSelectedStarter] = useState<StarterKey | null>(null);
  const [workflowOnlyOpening, setWorkflowOnlyOpening] = useState(false);
  const [snackbar, setSnackbar] = useState<LauncherSnackbar>(null);
  const [initialMrChatView, setInitialMrChatView] = useState<MrChatSessionView | null>(null);
  const [initialMrObserverNotificationId, setInitialMrObserverNotificationId] = useState<string | null>(null);
  const [initialMrNotificationBoundResumeId, setInitialMrNotificationBoundResumeId] = useState<string | null>(null);
  const [notificationBoundResume, setNotificationBoundResume] = useState(false);
  const [initialGpcRouter, setInitialGpcRouter] = useState<GpcRouterResponse | null>(null);
  const [initialConstructionSowChatView, setInitialConstructionSowChatView] = useState<import('../ConstructionSowChat/types').ConstructionSowChatSessionView | null>(null);
  const [initialWorkflowOnlyChoice, setInitialWorkflowOnlyChoice] = useState<Record<string, unknown> | null>(null);
  const [initialWorkflowOnlyPurchaseGuidance, setInitialWorkflowOnlyPurchaseGuidance] = useState<HomeChatGuidanceCard | null>(null);
  const [initialWorkflowOnlyGuidance, setInitialWorkflowOnlyGuidance] = useState<string | null>(null);
  const [initialWorkflowOnlyMessage, setInitialWorkflowOnlyMessage] = useState<string | null>(null);
  const [workflowOnlyEntry, setWorkflowOnlyEntry] = useState<ConstrainedWorkflowEntryState | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const inlineChatRef = useRef<HTMLDivElement | null>(null);
  const focusComposerAfterCloseRef = useRef(false);
  const focusConversationAfterOpenRef = useRef(false);
  const workflowOnlyEntryAttemptRef = useRef(0);

  const effectiveMode = parseHomeChatMode(homeChatMode, canUseChatbot);
  const workflowOnly = effectiveMode === 'workflow_only';
  // A CE does not receive general Home Chat. This server-issued flag admits
  // only one opaque notification-bound interrupted-resume projection.
  const canEnterShell = effectiveMode !== 'none' || notificationBoundResume;

  useEffect(() => {
    const resume = (event: Event) => {
      const prompt = (event as CustomEvent<{ surface: string; context_id?: string }>).detail;
      if (prompt?.surface !== 'homechat') return;
      setInitialMessage(null);
      setInitialSessionId(prompt.context_id ?? null);
      setChatOpen(true);
    };
    window.addEventListener('chat-feedback-recovery-submitted', resume);
    return () => window.removeEventListener('chat-feedback-recovery-submitted', resume);
  }, []);

  const visibleStarters = useMemo(
    () => HOME_CHAT_STARTERS.filter((starter) => (
      starter.key === 'ask'
      || !allowedWorkflowTypes
      || starter.workflowTypes.some((type) => allowedWorkflowTypes.includes(type))
    )),
    [allowedWorkflowTypes],
  );

  const workflowOnlyPresentation = useMemo(
    () => getWorkflowOnlyPresentation(allowedWorkflowTypes, workflowOnlyAudience),
    [allowedWorkflowTypes, workflowOnlyAudience],
  );
  const workflowOnlyStarters = useMemo(() => [...HOME_CHAT_STARTERS, ...CONSTRAINED_CONSTRUCTION_STARTERS].filter((starter) => (
    (starter.key === 'commodities' && workflowOnlyPresentation?.abilities.includes('market_research')
      && (!allowedWorkflowTypes || allowedWorkflowTypes.includes('market_research')))
    || (starter.key === 'gpc' && workflowOnlyPresentation?.abilities.includes('gpc')
      && (!allowedWorkflowTypes || allowedWorkflowTypes.includes('gpc')))
    || (starter.key === 'construction_generate'
      && workflowOnlyPresentation?.abilities.includes('construction')
      && allowedWorkflowTypes?.includes('one_time_construction'))
    || (starter.key === 'price_estimator' && workflowOnlyAudience === 'ra'
      && workflowOnlyPresentation?.abilities.includes('construction')
      && allowedWorkflowTypes?.includes('one_time_construction'))
    || (starter.key === 'construction_verify'
      && workflowOnlyPresentation?.abilities.includes('construction')
      && allowedWorkflowTypes?.includes('sow_verification'))
  )), [allowedWorkflowTypes, workflowOnlyAudience, workflowOnlyPresentation]);

  const focusDraftAtEnd = useCallback((value: string) => {
    // Focus synchronously so the starter click itself owns focus. The frame
    // callback runs after React applies the new draft and moves the caret to
    // the end of that value.
    inputRef.current?.focus();
    window.requestAnimationFrame(() => {
      const input = inputRef.current;
      input?.focus();
      input?.setSelectionRange(value.length, value.length);
    });
  }, []);

  const handleStarterClick = useCallback((starter: StarterAction) => {
    setSelectedStarter(starter.key);
    setDraft(starter.prompt);
    focusDraftAtEnd(starter.prompt);
  }, [focusDraftAtEnd]);

  const handleClose = useCallback(() => {
    workflowOnlyEntryAttemptRef.current += 1;
    setChatOpen(false);
    setExamplesOpen(false);
    setInitialMessage(null);
    setInitialSessionId(null);
    setInitialMrChatView(null);
    setInitialMrObserverNotificationId(null);
    setInitialMrNotificationBoundResumeId(null);
    setNotificationBoundResume(false);
    setInitialGpcRouter(null);
    setInitialConstructionSowChatView(null);
    setInitialWorkflowOnlyChoice(null);
    setInitialWorkflowOnlyPurchaseGuidance(null);
    setInitialWorkflowOnlyGuidance(null);
    setInitialWorkflowOnlyMessage(null);
    setWorkflowOnlyEntry(null);
    setWorkflowOnlyOpening(false);
  }, []);

  const handleWorkflowOnlySubmit = useCallback(async (message: string) => {
    if (readOnly || workflowOnlyOpening) return;
    const attempt = ++workflowOnlyEntryAttemptRef.current;
    setWorkflowOnlyOpening(true);
    setInitialMrChatView(null);
    setInitialMrObserverNotificationId(null);
    setInitialMrNotificationBoundResumeId(null);
    setNotificationBoundResume(false);
    setInitialGpcRouter(null);
    setInitialConstructionSowChatView(null);
    setInitialWorkflowOnlyChoice(null);
    setInitialWorkflowOnlyPurchaseGuidance(null);
    setInitialWorkflowOnlyGuidance(null);
    setInitialWorkflowOnlyMessage(null);
    setInitialMessage(null);
    setWorkflowOnlyEntry({ state: 'pending', message });
    setChatOpen(true);
    try {
      const created = await createSession();
      if (!created || typeof created.session_id !== 'string' || !created.session_id) {
        throw new Error('Unable to start this workflow.');
      }
      // Match the ordinary CO/CS lifecycle: the server session is real before
      // routing finishes, so hand it to HomeChatPanel immediately. The panel
      // can then render its standard Previous chats rail (including the
      // active zero-turn placeholder) while this constrained request runs.
      setInitialSessionId(created.session_id);
      const response = await routeConstrainedWorkflowTypedEntry(
        created.session_id, message,
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID() : `workflow-entry-route-${Date.now()}`,
      );
      const mrChat = extractMrRouterResponse(response);
      const gpcRouter = extractGpcRouterResponse(response);
      const constructionSowChat = extractConstructionSowRouterResponse(response);
      const nativeEntry = extractConstrainedNativeWorkflowEntry(response);
      const root = response && typeof response === 'object' ? response as Record<string, unknown> : null;
      if (attempt !== workflowOnlyEntryAttemptRef.current) return;
      if (mrChat) setInitialMrChatView(mrChat);
      else if (gpcRouter) setInitialGpcRouter(gpcRouter);
      else if (constructionSowChat) setInitialConstructionSowChatView(constructionSowChat);
      else if (nativeEntry && onStartWorkflowType) {
        onStartWorkflowType(nativeEntry.workflowType, {
          homeChatSessionId: created.session_id,
          workflowHandoffReceipt: nativeEntry.workflowHandoffReceipt,
          ...(nativeEntry.workflowEntryMode ? { workflowEntryMode: nativeEntry.workflowEntryMode } : {}),
        });
        handleClose();
        return;
      } else if (root?.status === 'workflow_only_choice' && isWorkflowOnlyChoice(root.chat_card)) {
        setInitialWorkflowOnlyChoice(root.chat_card);
      } else if (root?.chat_card && (normalizeHomeChatCards([root.chat_card])[0]?.kind === 'mission_partner_guidance'
        || (normalizeHomeChatCards([root.chat_card])[0]?.kind === 'purchase_guidance'
          && (workflowOnlyAudience === 'ra' || (workflowOnlyAudience !== 'ce'
            && workflowOnlyPresentation?.abilities.some((ability) => ability === 'market_research' || ability === 'gpc')))))) {
        setInitialWorkflowOnlyPurchaseGuidance(normalizeHomeChatCards([root.chat_card])[0] as HomeChatGuidanceCard);
      } else if (root?.status === 'workflow_only_guidance' && typeof root.detail === 'string') {
        setInitialWorkflowOnlyGuidance(root.detail);
      } else {
        throw new Error('Unable to route this workflow request.');
      }
      setInitialWorkflowOnlyMessage(message);
      // `HomeChatPanel` must mount only after all of its initial controller
      // props above are ready. Its controller children consume initial views
      // once, so clearing this pending shell is deliberately the final state
      // change in the successful route transaction.
      setWorkflowOnlyEntry(null);
    } catch {
      if (attempt !== workflowOnlyEntryAttemptRef.current) return;
      // Keep the real id allocated before routing. HomeChatPanel retains its
      // selected zero-turn rail row with the recoverable typed-entry error;
      // clearing it here made the entry appear to vanish after a failed route.
      setInitialMrChatView(null);
      setInitialMrObserverNotificationId(null);
      setInitialMrNotificationBoundResumeId(null);
      setNotificationBoundResume(false);
      setInitialGpcRouter(null);
      setInitialConstructionSowChatView(null);
      setInitialWorkflowOnlyChoice(null);
      setInitialWorkflowOnlyPurchaseGuidance(null);
      setInitialWorkflowOnlyGuidance(null);
      setInitialWorkflowOnlyMessage(null);
      // Do not collapse a constrained CE/RA shell into an empty page or a
      // transient snackbar. The user can retry the same safe route or return
      // to the constrained launcher; no general-chat surface is introduced.
      setWorkflowOnlyEntry({ state: 'error', message });
    } finally {
      if (attempt === workflowOnlyEntryAttemptRef.current) setWorkflowOnlyOpening(false);
    }
  }, [handleClose, onStartWorkflowType, readOnly, workflowOnlyOpening, workflowOnlyPresentation, workflowOnlyAudience]);

  const handleSubmit = useCallback((text: string, sessionId: string | null) => {
    setInitialMessage(text);
    setInitialSessionId(sessionId);
    setChatOpen(true);
    setSelectedStarter(null);
    return true;
  }, []);

  const handleOpenSessions = useCallback(() => {
    setInitialMessage(null);
    setInitialSessionId(null);
    focusConversationAfterOpenRef.current = true;
    setChatOpen(true);
  }, []);

  const handleActiveSessionIdChange = useCallback((sessionId: string | null) => {
    // Keep the launcher as the durable owner of the open conversation. The
    // panel can be re-rendered while recent workflows and chat history refresh;
    // retaining the server-issued id here prevents a completed lazy-create
    // response from falling back to an unselected, empty conversation.
    setInitialSessionId(sessionId);
    if (sessionId) {
      // The opening message has already been sent. Clearing it also makes a
      // future panel remount hydrate the conversation instead of sending the
      // same request a second time.
      setInitialMessage(null);
    }
  }, []);

  const handleNewRequest = useCallback(() => {
    focusComposerAfterCloseRef.current = true;
    handleClose();
  }, [handleClose]);

  const handleOpenWorkflow = useCallback((
    flowId: string,
    targetStageHint?: GpcChatWorkflowStageKey,
    context?: HomeChatHandoffContext,
  ) => {
    handleClose();
    onOpenWorkflow?.(flowId, targetStageHint, context);
  }, [handleClose, onOpenWorkflow]);

  const handleOpenMrWorkflow = useCallback((
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => {
    handleClose();
    onOpenMrWorkflow?.(flowId, context);
  }, [handleClose, onOpenMrWorkflow]);

  const handleOpenNavigationHandle = useCallback(async (
    navigationHandle: string,
    context?: HomeChatHandoffContext,
  ) => {
    if (!onOpenNavigationHandle) {
      throw new Error('This workflow is no longer available.');
    }
    await onOpenNavigationHandle(navigationHandle, context);
    handleClose();
  }, [handleClose, onOpenNavigationHandle]);

  const handleStartWorkflowType = useCallback((
    workflowType: WorkflowRecommendationType,
    context?: HomeChatHandoffContext,
  ) => {
    if (!onStartWorkflowType) {
      throw new Error('This workflow is no longer available.');
    }
    onStartWorkflowType(workflowType, context);
    handleClose();
  }, [handleClose, onStartWorkflowType]);

  const handleOpenCreatedWorkflow = useCallback(async (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => {
    if (!onOpenCreatedWorkflow) throw new Error('This workflow is no longer available.');
    await onOpenCreatedWorkflow(flowId, context);
    handleClose();
  }, [handleClose, onOpenCreatedWorkflow]);

  useEffect(() => {
    const handler = (event: Event) => {
      if ((event as CustomEvent).detail) {
        setInitialMessage(null);
        setInitialSessionId(null);
        setChatOpen(true);
      } else {
        handleClose();
      }
    };
    window.addEventListener('tour:set-home-chat', handler);
    return () => window.removeEventListener('tour:set-home-chat', handler);
  }, [handleClose]);

  useEffect(() => {
    const openWorkflowOnly = (event: Event) => {
      // A role preview intentionally has no session/controller projection to
      // avoid accepting a window event as a route into authenticated work.
      const detail = (event as CustomEvent<unknown>).detail;
      if (!detail || typeof detail !== 'object') return;
      const value = detail as { homeChatSessionId?: unknown; mrChat?: unknown; observerNotificationId?: unknown; notificationBoundResume?: unknown; notificationBoundResumeId?: unknown };
      const mrChat = value.mrChat as MrChatSessionView | undefined;
      const hasCanonicalPendingForm = Boolean(
        mrChat?.pending_input && Array.isArray(mrChat.pending_input.fields),
      );
      const isWaitingForExecution = (
        mrChat?.controller?.state === 'waiting_for_execution'
        && mrChat.pending_input === null
      );
      const isObserverRunning = (
        mrChat?.controller?.state === 'observer_running'
        && mrChat.pending_input === null
      );
      const isRunning = (
        mrChat?.controller?.state === 'running'
        && mrChat.pending_input === null
        && mrChat.progress?.status === 'in_progress'
        && typeof mrChat.progress?.current_operation === 'object'
        && mrChat.progress.current_operation !== null
      );
      const isInterrupted = mrChat?.controller?.state === 'interrupted'
        && mrChat.pending_input === null;
      const isSupportedDownstreamTerminal = (
        mrChat?.controller?.state === 'ready'
        && mrChat.workflow?.status === 'complete'
        && mrChat.cursor === 12
        && mrChat.pending_input === null
      );
      const isNotificationBoundResume = value.notificationBoundResume === true;
      // A full Home Chat user may open a canonical question form, a running
      // continuation, or a server-issued observer/bound resume projection.
      // Other event shapes still need the constrained shell.
      if (readOnly || (!workflowOnly && !hasCanonicalPendingForm && !isWaitingForExecution && !isRunning && !isObserverRunning
        && !isNotificationBoundResume && !isSupportedDownstreamTerminal)) return;
      if (
        typeof value.homeChatSessionId !== 'string' || !value.homeChatSessionId
        || !mrChat || typeof mrChat.session_id !== 'string'
        || typeof mrChat.workflow?.flow_id !== 'string'
        || (!hasCanonicalPendingForm && !isWaitingForExecution && !isObserverRunning && !isRunning
          && !isInterrupted && !isSupportedDownstreamTerminal)
        || (isObserverRunning && (typeof value.observerNotificationId !== 'string' || !value.observerNotificationId))
      ) return;
      setInitialMessage(null);
      setInitialSessionId(value.homeChatSessionId);
      setInitialMrChatView(mrChat);
      setInitialMrObserverNotificationId(isObserverRunning ? value.observerNotificationId as string : null);
      setInitialMrNotificationBoundResumeId(
        isNotificationBoundResume && typeof value.notificationBoundResumeId === 'string'
          ? value.notificationBoundResumeId : null,
      );
      setNotificationBoundResume(isNotificationBoundResume);
      // A mounted panel consumes its initial session only once. A fresh
      // notification must mount a fresh panel for this exact projection.
      setNotificationEntryVersion((current) => current + 1);
      setChatOpen(true);
    };
    window.addEventListener('home-chat:open-workflow-only', openWorkflowOnly);
    return () => window.removeEventListener('home-chat:open-workflow-only', openWorkflowOnly);
  }, [readOnly, workflowOnly]);

  useEffect(() => {
    const focusComposer = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: unknown }>).detail;
      if (typeof detail?.message === 'string') setDraft(detail.message);
      focusComposerAfterCloseRef.current = true;
      handleClose();
    };
    window.addEventListener('home-chat:focus', focusComposer);
    return () => window.removeEventListener('home-chat:focus', focusComposer);
  }, [handleClose]);

  useEffect(() => {
    if (chatOpen || examplesOpen || !focusComposerAfterCloseRef.current) return;
    focusComposerAfterCloseRef.current = false;
    focusDraftAtEnd(draft);
  }, [chatOpen, examplesOpen, draft, focusDraftAtEnd]);

  useEffect(() => {
    if (!chatOpen) return undefined;
    if (focusConversationAfterOpenRef.current) {
      focusConversationAfterOpenRef.current = false;
      const conversationInput = inlineChatRef.current?.querySelector(
        'textarea:not([aria-hidden="true"])',
      ) as HTMLTextAreaElement | null;
      conversationInput?.focus();
    }
    const frame = window.requestAnimationFrame(() => {
      inlineChatRef.current?.scrollIntoView?.({
        behavior: 'auto',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [chatOpen]);

  if (!canEnterShell || (workflowOnly && !workflowOnlyPresentation)) return null;

  if (workflowOnly) {
    return (
      <Box sx={{ width: '100%' }} data-testid="home-chat-workflow-only-launcher">
        {!chatOpen ? (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="h2" variant="h2" sx={{ fontSize: { xs: 28, sm: 36 }, lineHeight: 1.15 }}>
                {workflowOnlyPresentation!.launcherTitle}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                {workflowOnlyPresentation!.launcherSubtitle}
              </Typography>
            </Box>
            <HomeChatSearchBar
              onSubmit={(message) => { void handleWorkflowOnlySubmit(message); }}
              onError={(message) => setSnackbar({ severity: 'error', message })}
              draft={draft}
              onDraftChange={setDraft}
              inputRef={inputRef}
              allowAttachments={false}
              placeholder={workflowOnlyPresentation!.composerPlaceholder}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {workflowOnlyStarters.map((starter) => (
                <ButtonBase
                  key={starter.key}
                  aria-pressed={selectedStarter === starter.key}
                  disabled={readOnly || workflowOnlyOpening}
                  onClick={() => handleStarterClick(starter)}
                  sx={{ minHeight: 42, px: 1.5, py: 0.9, border: '1px solid', borderColor: selectedStarter === starter.key ? 'primary.main' : 'divider', borderRadius: 999 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', color: 'primary.main' }}>{starter.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>{starter.label}</Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
            <Button
              variant="text"
              sx={{ mt: 1.25 }}
              disabled={readOnly}
              onClick={handleOpenSessions}
            >
              Previous chats
            </Button>
          </>
        ) : (
          <Box ref={inlineChatRef} data-testid="home-chat-inline" sx={{ minWidth: 0, scrollMarginTop: 88 }}>
            <Box sx={{ height: { xs: 'calc(100vh - 210px)', md: 'min(720px, calc(100vh - 190px))' }, minHeight: { xs: 520, md: 600 }, overflow: 'hidden' }}>
              <HomeChatPanel
                key={notificationEntryVersion}
                initialMessage={initialMessage}
                initialSessionId={initialSessionId}
                initialMrChatView={initialMrChatView}
                initialMrObserverNotificationId={initialMrObserverNotificationId}
                initialMrNotificationBoundResumeId={initialMrNotificationBoundResumeId}
                initialGpcRouter={initialGpcRouter}
                initialConstructionSowChatView={initialConstructionSowChatView}
                initialWorkflowOnlyChoice={initialWorkflowOnlyChoice}
                initialWorkflowOnlyPurchaseGuidance={initialWorkflowOnlyPurchaseGuidance}
                initialWorkflowOnlyGuidance={initialWorkflowOnlyGuidance}
                initialWorkflowOnlyMessage={initialWorkflowOnlyMessage}
                constrainedWorkflowEntry={workflowOnlyEntry}
                onRetryConstrainedWorkflowEntry={(message) => { void handleWorkflowOnlySubmit(message); }}
                onBackConstrainedWorkflowEntry={(message) => {
                  setDraft(message);
                  handleClose();
                }}
                workflowOnly
                workflowOnlyAudience={workflowOnlyAudience}
                allowedWorkflowTypes={allowedWorkflowTypes}
                onActiveSessionIdChange={handleActiveSessionIdChange}
                onOpenWorkflow={handleOpenWorkflow}
                onOpenMrWorkflow={handleOpenMrWorkflow}
                onOpenConstructionSowWorkflow={(flowId, context) => {
                  handleClose();
                  onOpenConstructionSowWorkflow?.(flowId, context);
                }}
                onStartWorkflowType={handleStartWorkflowType}
              />
            </Box>
          </Box>
        )}
        <Snackbar
          open={snackbar !== null}
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {snackbar ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">{snackbar.message}</Alert> : undefined}
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <ChatExamplesBrowser
        showTrigger={false}
        open={examplesOpen}
        onClose={() => setExamplesOpen(false)}
        accessScope={examplesAccessScope}
        canUseChatbot={canUseChatbot}
        allowedWorkflowTypes={allowedWorkflowTypes}
        onOpenWorkflow={onOpenExampleWorkflow ? (id, title, type) => { setExamplesOpen(false); onOpenExampleWorkflow(id, title, type); } : undefined}
        onNewChat={() => { setExamplesOpen(false); handleNewRequest(); }}
        onOpenChats={() => { setExamplesOpen(false); setChatOpen(true); }}
      />
      <Box sx={{ display: examplesOpen ? 'none' : 'block' }}>
        <HomeChatCompletionReconciler
          activeSessionId={initialSessionId}
          onView={(sessionId) => {
            setInitialMessage(null);
            setInitialSessionId(sessionId);
            setChatOpen(true);
          }}
        />
        {/* Keep the composer mounted while chat history is open so its draft
            session and staged attachment chips survive the round trip. */}
        <Box sx={{ display: chatOpen ? 'none' : 'block' }}>
          <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="h2"
              variant="h2"
              sx={{ fontSize: { xs: 24, sm: 36 }, lineHeight: { xs: '29px', sm: 1.15 } }}
            >
              What are you working on?
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Describe the outcome you need. The assistant will keep the work
              in chat until a structured workflow is useful.
            </Typography>
          </Box>

          <HomeChatSearchBar
            onSubmit={handleSubmit}
            onError={(message) => setSnackbar({ severity: 'error', message })}
            draft={draft}
            onDraftChange={setDraft}
            inputRef={inputRef}
            placeholder={
              selectedStarter === 'ask'
                ? 'Ask a question about anything in federal procurement…'
                : 'Describe a purchase, requirement, or procurement question…'
            }
          />

          <Box
            data-tour-id="home-tour-starters"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}
          >
            {canAccessTraining && (
                <ButtonBase aria-label="Training" onClick={onOpenTraining} sx={{ minHeight: { xs: 44, sm: 42 }, px: 1.5, py: 0.9, borderRadius: 1, border: '1px solid', borderColor: 'primary.main', '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <SchoolOutlinedIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>Training</Typography>
                  </Box>
                </ButtonBase>
            )}
            {visibleStarters.map((starter) => {
              const selected = selectedStarter === starter.key;
              return (
                <ButtonBase
                  key={starter.key}
                  aria-pressed={selected}
                  onClick={() => handleStarterClick(starter)}
                  sx={{
                    minHeight: { xs: 44, sm: 42 },
                    px: 1.5,
                    py: 0.9,
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderRadius: 999,
                    color: selected ? 'primary.main' : 'text.primary',
                    bgcolor: (theme) => selected
                      ? alpha(theme.palette.primary.main, 0.1)
                      : theme.palette.background.paper,
                    transition: 'border-color 160ms ease, background-color 160ms ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                    '&:focus-visible': {
                      outline: '3px solid',
                      outlineColor: (theme) => alpha(theme.palette.primary.main, 0.28),
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', color: 'primary.main' }}>
                      {starter.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      {starter.label}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Button
              onClick={handleOpenSessions}
              sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44, sm: 'auto' } }}
            >
              Previous chats
            </Button>
            <Button
              onClick={() => setExamplesOpen(true)}
              sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44, sm: 'auto' } }}
            >
              Chat examples
            </Button>
          </Box>
        </Box>

        <Box data-chat-feedback-recovery-host />

        {chatOpen && (
          <Box
            ref={inlineChatRef}
            data-testid="home-chat-inline"
            sx={{ minWidth: 0, scrollMarginTop: 88 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                mb: 1.5,
              }}
            >
              <Typography variant="h2" sx={{ fontSize: { xs: 24, sm: 30 } }}>
                Procurement Assistant
              </Typography>
              <Button
                onClick={handleNewRequest}
                startIcon={<ArrowBackIcon />}
                size="small"
              >
                New request
              </Button>
            </Box>
            <Box
              data-tour-id="home-chat-inline-panel"
              sx={{
                height: { xs: 'calc(100dvh - 210px)', sm: 'calc(100vh - 210px)', md: 'min(720px, calc(100vh - 190px))' },
                minHeight: { xs: 320, sm: 520, md: 600 },
                overflow: 'hidden',
              }}
            >
              <HomeChatPanel
                key={notificationEntryVersion}
                onOpenChatExamples={() => setExamplesOpen(true)}
                onOpenTraining={onOpenTrainingSuggestion}
                initialMessage={initialMessage}
                initialSessionId={initialSessionId}
                initialMrChatView={initialMrChatView}
                initialMrObserverNotificationId={initialMrObserverNotificationId}
                initialMrNotificationBoundResumeId={initialMrNotificationBoundResumeId}
                enableCompletionSound
                onActiveSessionIdChange={handleActiveSessionIdChange}
                onOpenWorkflow={handleOpenWorkflow}
                onOpenMrWorkflow={handleOpenMrWorkflow}
                onOpenConstructionSowWorkflow={(flowId, context) => {
                  handleClose();
                  onOpenConstructionSowWorkflow?.(flowId, context);
                }}
                onOpenGeneratedServicesPwsWorkflow={(flowId, context) => {
                  handleClose();
                  onOpenGeneratedServicesPwsWorkflow?.(flowId, context);
                }}
                onOpenNavigationHandle={handleOpenNavigationHandle}
                onOpenCreatedWorkflow={handleOpenCreatedWorkflow}
                onStartWorkflowType={handleStartWorkflowType}
              />
            </Box>
          </Box>
        )}

      </Box>
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default HomeChatLauncher;
